/**
 * DOMÍNIO — IMPORTAÇÃO EM LOTE DE EQUIPAMENTOS DE END (Bloco 18)
 *
 * Regras de parsing, normalização, validação, deduplicação e classificação
 * para importação em lote de equipamentos físicos de END:
 * 1. Aparelho de ME-US (ULTRASONIC_THICKNESS_GAUGE)
 * 2. Transdutores (ULTRASONIC_TRANSDUCER)
 * 3. Yokes (MAGNETIC_YOKE)
 * 4. Aparelhos B-Scan (BSCAN_EQUIPMENT)
 * 5. Bloco de Calibração (CALIBRATION_BLOCK)
 *
 * Itens não-físicos (Procedimentos, Normas, Critérios, Consumíveis) não são
 * importados por esta rotina.
 */

import type { NdtAsset, NdtAssetType, CalibrationBlockStep } from "./ndt-assets.js";
import type { NdtAssetCertificate, NdtCertificateStatus } from "./ndt-asset-history.js";
import { resolveCalibrationStatus } from "./ndt-calibration.js";

// ============================================================================
// TIPOS E STATUS DE IMPORTAÇÃO
// ============================================================================

export type NdtImportRowStatus =
  | "NEW"              // Equipamento novo (será criado)
  | "UPDATE"           // Equipamento já existe, dados técnicos alterados
  | "NEW_CALIBRATION"  // Equipamento já existe, possui novo certificado de calibração
  | "UNCHANGED"        // Equipamento já existe com dados e calibração idênticos
  | "WARNING"          // Aviso não impeditivo
  | "ERROR";           // Erro estrutural ou de validação impeditivo

export interface NdtImportIssue {
  readonly rowNumber: number;
  readonly column?: string | undefined;
  readonly severity: "ERROR" | "WARNING";
  readonly message: string;
}

export interface NdtParsedCertificateDraft {
  readonly certificateNumber: string;
  readonly issueDate?: string | undefined;      // YYYY-MM-DD
  readonly expirationDate?: string | undefined; // YYYY-MM-DD
  readonly laboratory?: string | undefined;
  readonly status: NdtCertificateStatus;
}

export interface NdtImportRowDraft {
  readonly rowNumber: number;
  readonly sheetName: string;
  readonly assetType: NdtAssetType;

  // Identificação básica
  readonly internalCode: string;
  readonly serialNumber: string;
  readonly manufacturer: string;
  readonly model: string;
  readonly description: string;
  readonly patrimonyNumber?: string | undefined;
  readonly notes?: string | undefined;

  // Dados técnicos específicos
  readonly technicalData: Record<string, any>;
  readonly steps?: CalibrationBlockStep[] | undefined;

  // Certificado de calibração vinculado (se presente)
  readonly certificate?: NdtParsedCertificateDraft | undefined;

  // Matriz de valores originais brutos para rastreabilidade
  readonly rawValues: Record<string, string>;
}

export interface NdtImportStagedItem {
  readonly rowNumber: number;
  readonly sheetName: string;
  readonly assetType: NdtAssetType;
  readonly draft: NdtImportRowDraft;
  readonly status: NdtImportRowStatus;
  readonly issues: readonly NdtImportIssue[];
  readonly existingAsset?: NdtAsset | undefined;
  readonly existingCertificate?: NdtAssetCertificate | null | undefined;
  readonly changesDescription?: string[] | undefined;
  readonly isBlocked: boolean;
}

export interface NdtImportSummary {
  readonly totalRows: number;
  readonly totalValid: number;
  readonly totalErrors: number;
  readonly totalWarnings: number;
  readonly countNew: number;
  readonly countUpdate: number;
  readonly countNewCalibration: number;
  readonly countUnchanged: number;
  readonly bySheet: Record<string, {
    sheetName: string;
    assetType: NdtAssetType;
    total: number;
    valid: number;
    errors: number;
  }>;
}

export interface NdtImportExecutionResult {
  readonly success: boolean;
  readonly totalProcessed: number;
  readonly createdCount: number;
  readonly updatedCount: number;
  readonly newCertificatesCount: number;
  readonly unchangedCount: number;
  readonly skippedErrorsCount: number;
  readonly errors: Array<{ rowNumber: number; sheetName: string; message: string }>;
  readonly createdAssetIds: string[];
  readonly updatedAssetIds: string[];
}

// ============================================================================
// MAPEAMENTO DE ABAS E SINÔNIMOS
// ============================================================================

export interface NdtSheetMapping {
  readonly assetType: NdtAssetType;
  readonly defaultSheetName: string;
  readonly sheetAliases: readonly string[];
  readonly label: string;
}

export const NDT_EQUIPMENT_SHEET_MAPPINGS: readonly NdtSheetMapping[] = [
  {
    assetType: "ULTRASONIC_THICKNESS_GAUGE",
    defaultSheetName: "01_ME-US",
    sheetAliases: ["01_me-us", "me-us", "me_us", "aparelho de me-us", "aparelho me-us", "espessimetro", "medidor de espessura"],
    label: "Aparelho de ME-US"
  },
  {
    assetType: "ULTRASONIC_TRANSDUCER",
    defaultSheetName: "02_TRANSDUTORES",
    sheetAliases: ["02_transdutores", "transdutores", "transdutor", "cabecote", "cabecotes", "palpador", "probe"],
    label: "Transdutores"
  },
  {
    assetType: "MAGNETIC_YOKE",
    defaultSheetName: "03_YOKES",
    sheetAliases: ["03_yokes", "yokes", "yoke", "yoke magnetico", "eletroima"],
    label: "Yokes"
  },
  {
    assetType: "BSCAN_EQUIPMENT",
    defaultSheetName: "04_B-SCAN",
    sheetAliases: ["04_b-scan", "04_b_scan", "b-scan", "b_scan", "bscan", "aparelhos b-scan", "aparelho b-scan", "scanner b-scan"],
    label: "Aparelhos B-Scan"
  },
  {
    assetType: "CALIBRATION_BLOCK",
    defaultSheetName: "05_BLOCOS_CALIBRACAO",
    sheetAliases: ["05_blocos_calibracao", "blocos_calibracao", "bloco de calibracao", "blocos de calibracao", "bloco escalonado", "bloco degraus", "bloco v1", "bloco v2"],
    label: "Bloco de Calibração"
  },
  {
    assetType: "PYROMETER",
    defaultSheetName: "06_PIROMETROS",
    sheetAliases: ["06_pirometros", "pirometros", "pirometro", "medidor de temperatura infravermelho"],
    label: "Pirômetro"
  }
];

export function resolveSheetAssetType(sheetName: string): NdtAssetType | null {
  const norm = normalizeText(sheetName);
  for (const m of NDT_EQUIPMENT_SHEET_MAPPINGS) {
    if (norm === normalizeText(m.defaultSheetName)) return m.assetType;
    if (m.sheetAliases.some(alias => norm === normalizeText(alias) || norm.includes(normalizeText(alias)))) {
      return m.assetType;
    }
  }
  return null;
}

// ============================================================================
// NORMALIZADORES DE TEXTO, NÚMEROS E DATAS
// ============================================================================

export function normalizeText(val: unknown): string {
  if (val === null || val === undefined) return "";
  return String(val)
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toLowerCase();
}

export function parseNumberField(val: unknown): number | undefined {
  if (val === null || val === undefined) return undefined;
  const str = String(val).trim().replace(/\s+/g, "");
  if (!str) return undefined;

  // Trata formato pt-BR "1.234,56" ou "12,5" vs formato US "12.5"
  let clean = str;
  if (str.includes(",") && str.includes(".")) {
    // 1.250,50 -> 1250.50
    clean = str.replace(/\./g, "").replace(",", ".");
  } else if (str.includes(",")) {
    clean = str.replace(",", ".");
  }

  const num = Number(clean);
  return Number.isFinite(num) ? num : undefined;
}

/**
 * Normaliza datas para o formato ISO YYYY-MM-DD.
 * Suporta DD/MM/YYYY, DD-MM-YYYY, YYYY-MM-DD, YYYY/MM/DD e número de série Excel.
 */
export function parseDateField(val: unknown): string | undefined {
  if (val === null || val === undefined) return undefined;
  const str = String(val).trim();
  if (!str) return undefined;

  // Número serial Excel (dias desde 1899-12-30)
  if (/^\d{5}$/.test(str)) {
    const serial = Number(str);
    if (serial > 20000 && serial < 80000) {
      const utcDays = serial - 25569;
      const ms = utcDays * 86400 * 1000;
      const d = new Date(ms);
      if (!isNaN(d.getTime())) {
        return d.toISOString().slice(0, 10);
      }
    }
  }

  // DD/MM/YYYY ou DD-MM-YYYY
  const brMatch = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(str);
  if (brMatch && brMatch[1] && brMatch[2] && brMatch[3]) {
    const day = brMatch[1].padStart(2, "0");
    const month = brMatch[2].padStart(2, "0");
    const year = brMatch[3];
    const iso = `${year}-${month}-${day}`;
    const test = new Date(`${iso}T00:00:00Z`);
    if (!isNaN(test.getTime())) return iso;
  }

  // YYYY-MM-DD ou YYYY/MM/DD
  const isoMatch = /^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/.exec(str);
  if (isoMatch && isoMatch[1] && isoMatch[2] && isoMatch[3]) {
    const year = isoMatch[1];
    const month = isoMatch[2].padStart(2, "0");
    const day = isoMatch[3].padStart(2, "0");
    const iso = `${year}-${month}-${day}`;
    const test = new Date(`${iso}T00:00:00Z`);
    if (!isNaN(test.getTime())) return iso;
  }

  // ISO string completa
  const direct = new Date(str);
  if (!isNaN(direct.getTime())) {
    return direct.toISOString().slice(0, 10);
  }

  return undefined;
}

// ============================================================================
// DICIONÁRIO DE CABEÇALHOS E VALORES DE DOMÍNIO
// ============================================================================

export function findValueByHeaderKeywords(row: Record<string, string>, ...keywords: string[]): string {
  const normKeywords = keywords.map(normalizeText);
  const entries = Object.entries(row);

  // Busca exata primeiro
  for (const [col, val] of entries) {
    const normCol = normalizeText(col);
    if (normKeywords.some(kw => normCol === kw)) {
      return String(val ?? "").trim();
    }
  }

  // Busca parcial
  for (const [col, val] of entries) {
    const normCol = normalizeText(col);
    if (normKeywords.some(kw => normCol.includes(kw))) {
      return String(val ?? "").trim();
    }
  }

  return "";
}

export function normalizeTransducerType(val: string): string {
  const norm = normalizeText(val);
  if (!norm) return "DUAL_ELEMENT";
  if (norm.includes("duplo") || norm.includes("dual") || norm.includes("duplo cristal") || norm.includes("se")) return "DUAL_ELEMENT";
  if (norm.includes("mono") || norm.includes("single") || norm.includes("simples")) return "SINGLE_ELEMENT";
  if (norm.includes("angular") || norm.includes("angle") || norm.includes("inclinado")) return "ANGLE_BEAM";
  if (norm.includes("linha de atraso") || norm.includes("delay") || norm.includes("atraso")) return "DELAY_LINE";
  if (norm.includes("imersao") || norm.includes("immersion")) return "IMMERSION";
  return val.trim();
}

export function normalizeYokePowerType(val: string): "AC" | "DC" | "AC/DC" {
  const norm = normalizeText(val);
  if (norm.includes("ac/dc") || norm.includes("ac-dc") || norm.includes("ca/cc") || (norm.includes("ac") && norm.includes("dc"))) return "AC/DC";
  if (norm.includes("dc") || norm.includes("cc") || norm.includes("continua")) return "DC";
  return "AC";
}

export function parseStepThicknesses(raw: string): CalibrationBlockStep[] {
  if (!raw || !raw.trim()) return [];
  // Divide por vírgula, ponto e vírgula, barra ou espaço
  const parts = raw.split(/[,;/|\s]+/).map(p => p.trim()).filter(Boolean);
  const steps: CalibrationBlockStep[] = [];
  let seq = 1;

  for (const p of parts) {
    const num = parseNumberField(p);
    if (num !== undefined && num > 0) {
      steps.push({
        id: `STEP-${seq}`,
        stepNumber: String(seq),
        nominalThickness: num,
        unit: "mm",
        sequence: seq,
        notes: ""
      });
      seq++;
    }
  }

  return steps;
}

// ============================================================================
// MAPEAMENTO E VALIDAÇÃO DE LINHA
// ============================================================================

export function parseImportRow(
  rowNumber: number,
  sheetName: string,
  assetType: NdtAssetType,
  rawValues: Record<string, string>
): { draft: NdtImportRowDraft; issues: NdtImportIssue[] } {
  const issues: NdtImportIssue[] = [];

  // Campos de identificação universais
  const internalCode = findValueByHeaderKeywords(rawValues, "codigo interno", "codigo", "tag", "identificacao", "id interno", "cod");
  const serialNumber = findValueByHeaderKeywords(rawValues, "numero de serie", "numero serie", "n serie", "no serie", "serial", "serie", "n de serie", "identificacao bloco");
  const manufacturer = findValueByHeaderKeywords(rawValues, "fabricante", "marca", "manufacturer", "fornecedor");
  const model = findValueByHeaderKeywords(rawValues, "modelo", "model", "tipo modelo");
  const description = findValueByHeaderKeywords(rawValues, "descricao", "nome", "equipamento", "description");
  const patrimonyNumber = findValueByHeaderKeywords(rawValues, "patrimonio", "no patrimonio", "n patrimonio", "tag patrimonio") || undefined;
  const notes = findValueByHeaderKeywords(rawValues, "observacoes", "observacao", "notas", "obs", "notes") || undefined;

  // Validação dos campos obrigatórios mínimos
  if (!internalCode && !serialNumber) {
    issues.push({
      rowNumber,
      column: "Código Interno / Nº Série",
      severity: "ERROR",
      message: "A linha deve possuir ao menos 'Código Interno' ou 'Nº de Série' para identificação."
    });
  }

  // Certificado de Calibração
  const certNumber = findValueByHeaderKeywords(rawValues, "numero do certificado", "numero certificado", "no certificado", "n certificado", "certificado de calibracao", "certificado calibracao", "certificado", "no cert", "n cert");
  const rawCalibDate = findValueByHeaderKeywords(rawValues, "data da calibracao", "data calibracao", "data emissao", "data certificado", "data de emissao", "emissao");
  const rawExpDate = findValueByHeaderKeywords(rawValues, "data de validade", "data validade", "validade", "vencimento", "data vencimento", "proxima calibracao");
  const lab = findValueByHeaderKeywords(rawValues, "orgao emissor", "laboratorio emissor", "laboratorio", "orgao", "lab") || undefined;

  let certificate: NdtParsedCertificateDraft | undefined = undefined;

  if (certNumber || rawCalibDate || rawExpDate) {
    const issueDate = rawCalibDate ? parseDateField(rawCalibDate) : undefined;
    const expirationDate = rawExpDate ? parseDateField(rawExpDate) : undefined;

    if (rawCalibDate && !issueDate) {
      issues.push({
        rowNumber,
        column: "Data da Calibração",
        severity: "WARNING",
        message: `Formato de Data da Calibração '${rawCalibDate}' não reconhecido. Use DD/MM/AAAA ou AAAA-MM-DD.`
      });
    }

    if (rawExpDate && !expirationDate) {
      issues.push({
        rowNumber,
        column: "Data de Validade",
        severity: "WARNING",
        message: `Formato de Data de Validade '${rawExpDate}' não reconhecido. Use DD/MM/AAAA ou AAAA-MM-DD.`
      });
    }

    // Validação lógica: data de calibração não pode ser posterior à validade
    if (issueDate && expirationDate && issueDate > expirationDate) {
      issues.push({
        rowNumber,
        column: "Validade vs Calibração",
        severity: "ERROR",
        message: `Data de Calibração (${issueDate}) não pode ser posterior à Data de Validade (${expirationDate}).`
      });
    }

    const certStatus = resolveCalibrationStatus(expirationDate);
    certificate = {
      certificateNumber: certNumber || "NÃO INFORMADO",
      issueDate,
      expirationDate,
      laboratory: lab,
      status: certStatus
    };
  }

  // Dados técnicos específicos por tipo
  const technicalData: any = {};
  let steps: CalibrationBlockStep[] | undefined = undefined;

  if (assetType === "ULTRASONIC_THICKNESS_GAUGE") {
    const rangeMin = parseNumberField(findValueByHeaderKeywords(rawValues, "faixa medicao min", "faixa minima", "faixa min", "range min", "min (mm)"));
    const rangeMax = parseNumberField(findValueByHeaderKeywords(rawValues, "faixa medicao max", "faixa maxima", "faixa max", "range max", "max (mm)"));
    const soundVel = parseNumberField(findValueByHeaderKeywords(rawValues, "velocidade sonora", "velocidade som", "velocidade (m/s)", "sound velocity"));
    const resolution = parseNumberField(findValueByHeaderKeywords(rawValues, "resolucao", "resolucao (mm)", "resolution"));

    if (rangeMin !== undefined && rangeMax !== undefined && rangeMin > rangeMax) {
      issues.push({
        rowNumber,
        column: "Faixa de Medição",
        severity: "ERROR",
        message: `Faixa mínima (${rangeMin} mm) não pode ser superior à faixa máxima (${rangeMax} mm).`
      });
    }

    technicalData.measurementRangeMin = rangeMin;
    technicalData.measurementRangeMax = rangeMax;
    technicalData.soundVelocityMin = soundVel;
    technicalData.soundVelocityMax = soundVel;
    technicalData.resolution = resolution;
    technicalData.measurementUnit = "mm";
    technicalData.soundVelocityUnit = "m/s";

  } else if (assetType === "ULTRASONIC_TRANSDUCER") {
    const rawType = findValueByHeaderKeywords(rawValues, "tipo transdutor", "tipo de transdutor", "tipo", "transducer type");
    const freq = parseNumberField(findValueByHeaderKeywords(rawValues, "frequencia (mhz)", "frequencia mhz", "frequencia", "frequency"));
    const dim = parseNumberField(findValueByHeaderKeywords(rawValues, "diametro (mm)", "dimensao (mm)", "diametro", "dimensao", "element diameter", "cristal (mm)"));
    const angle = parseNumberField(findValueByHeaderKeywords(rawValues, "angulo nominal", "angulo (o)", "angulo", "angle"));
    const connector = findValueByHeaderKeywords(rawValues, "conector", "tipo conector", "connector type");
    const appType = findValueByHeaderKeywords(rawValues, "aplicacao", "tipo de aplicacao", "application");

    technicalData.transducerType = rawType ? normalizeTransducerType(rawType) : "DUAL_ELEMENT";
    technicalData.frequencyMHz = freq;
    technicalData.elementDiameter = dim;
    technicalData.dimensionUnit = "mm";
    technicalData.nominalAngle = angle;
    technicalData.connectorType = connector || undefined;
    technicalData.applicationType = appType || undefined;

  } else if (assetType === "MAGNETIC_YOKE") {
    const rawPower = findValueByHeaderKeywords(rawValues, "tipo corrente", "tipo de corrente", "alimentacao", "corrente", "power type");
    const voltage = findValueByHeaderKeywords(rawValues, "tensao alimentacao", "tensao (v)", "tensao", "voltagem", "voltage");
    const poleMin = parseNumberField(findValueByHeaderKeywords(rawValues, "espacamento polos min", "espacamento min", "pole spacing min"));
    const poleMax = parseNumberField(findValueByHeaderKeywords(rawValues, "espacamento polos max", "espacamento max", "pole spacing max"));
    const weight = parseNumberField(findValueByHeaderKeywords(rawValues, "peso proprio", "peso (kg)", "peso", "weight"));

    technicalData.powerType = normalizeYokePowerType(rawPower);
    technicalData.supplyVoltage = voltage || undefined;
    technicalData.poleSpacingMin = poleMin;
    technicalData.poleSpacingMax = poleMax;
    technicalData.weight = weight;

  } else if (assetType === "BSCAN_EQUIPMENT") {
    const tech = findValueByHeaderKeywords(rawValues, "tecnologia", "tipo scanner", "scanner", "technology");
    const rangeMin = parseNumberField(findValueByHeaderKeywords(rawValues, "faixa min", "range min"));
    const rangeMax = parseNumberField(findValueByHeaderKeywords(rawValues, "faixa max", "range max"));
    const resolution = parseNumberField(findValueByHeaderKeywords(rawValues, "resolucao encoder", "resolucao (mm)", "resolution"));
    const swVersion = findValueByHeaderKeywords(rawValues, "versao software", "versao", "software version");

    technicalData.technology = tech || "AUTOMATED_SCANNER";
    technicalData.measurementRangeMin = rangeMin;
    technicalData.measurementRangeMax = rangeMax;
    technicalData.resolution = resolution;
    technicalData.softwareVersion = swVersion || undefined;

  } else if (assetType === "PYROMETER") {
    const rangeMin = parseNumberField(findValueByHeaderKeywords(rawValues, "faixa temperatura minima", "faixa medicao min", "faixa min", "range min"));
    const rangeMax = parseNumberField(findValueByHeaderKeywords(rawValues, "faixa temperatura maxima", "faixa medicao max", "faixa max", "range max"));
    const emissivity = parseNumberField(findValueByHeaderKeywords(rawValues, "emissividade", "emissivity"));
    const resolution = parseNumberField(findValueByHeaderKeywords(rawValues, "resolucao", "resolution"));
    if (rangeMin !== undefined && rangeMax !== undefined && rangeMin > rangeMax) {
      issues.push({ rowNumber, column: "Faixa de Temperatura", severity: "ERROR",
        message: "A temperatura mínima não pode ultrapassar a máxima." });
    }
    if (emissivity !== undefined && (emissivity < 0 || emissivity > 1)) {
      issues.push({ rowNumber, column: "Emissividade", severity: "ERROR",
        message: "A emissividade deve estar entre 0 e 1." });
    }
    technicalData.measurementRangeMin = rangeMin;
    technicalData.measurementRangeMax = rangeMax;
    technicalData.measurementUnit = "°C";
    technicalData.resolution = resolution;
    technicalData.emissivity = emissivity;
    technicalData.accuracy = findValueByHeaderKeywords(rawValues, "exatidao", "precisao", "accuracy");
    technicalData.distanceToSpotRatio = findValueByHeaderKeywords(rawValues, "distancia ponto", "distance to spot");

  } else if (assetType === "CALIBRATION_BLOCK") {
    const mat = findValueByHeaderKeywords(rawValues, "material do bloco", "material bloco", "material", "block material");
    const thick = parseNumberField(findValueByHeaderKeywords(rawValues, "espessura total", "espessura (mm)", "overall thickness"));
    const rawSteps = findValueByHeaderKeywords(rawValues, "espessuras degraus", "espessuras dos degraus (mm)", "degraus (mm)", "espessuras dos degraus", "degraus", "steps");

    technicalData.material = mat || "Aço Carbono";
    technicalData.overallThickness = thick;
    technicalData.blockType = "ESCALONADO_DE_DEGRAUS";

    if (rawSteps) {
      steps = parseStepThicknesses(rawSteps);
    }
  }

  const draft: NdtImportRowDraft = {
    rowNumber,
    sheetName,
    assetType,
    internalCode: internalCode || serialNumber,
    serialNumber: serialNumber || internalCode,
    manufacturer: manufacturer || "NÃO INFORMADO",
    model: model || "NÃO INFORMADO",
    description: description || `${assetType} ${internalCode || serialNumber}`,
    patrimonyNumber,
    notes,
    technicalData,
    steps,
    certificate,
    rawValues
  };

  return { draft, issues };
}

// ============================================================================
// COMPARADOR E CLASSIFICADOR DE STAGING
// ============================================================================

export function matchAssetWithDatabase(
  draft: NdtImportRowDraft,
  existingAssets: readonly NdtAsset[]
): NdtAsset | undefined {
  const normSerial = normalizeText(draft.serialNumber);
  const normCode = normalizeText(draft.internalCode);
  const normMfg = normalizeText(draft.manufacturer);
  const normModel = normalizeText(draft.model);

  // Filtra apenas ativos do mesmo tipo
  const sameType = existingAssets.filter(a => a.assetType === draft.assetType);

  // 1. Match exato por Nº de Série (se presente e significativo)
  if (normSerial && normSerial !== "nao informado" && normSerial !== "-") {
    const match = sameType.find(a => normalizeText(a.serialNumber) === normSerial);
    if (match) return match;
  }

  // 2. Match exato por Código Interno
  if (normCode && normCode !== "nao informado" && normCode !== "-") {
    const match = sameType.find(a => normalizeText(a.internalCode) === normCode);
    if (match) return match;
  }

  // 3. Match composto Fabricante + Modelo (se ambos únicos)
  if (normMfg && normModel && normMfg !== "nao informado" && normModel !== "nao informado") {
    const matches = sameType.filter(a =>
      normalizeText(a.manufacturer) === normMfg &&
      normalizeText(a.model) === normModel
    );
    if (matches.length === 1) return matches[0];
  }

  return undefined;
}

export function classifyStagedRow(
  draft: NdtImportRowDraft,
  issues: readonly NdtImportIssue[],
  existingAsset: NdtAsset | undefined,
  existingCertificates: readonly NdtAssetCertificate[]
): NdtImportStagedItem {
  const currentIssues = [...issues];
  if (!draft.manufacturer || draft.manufacturer.trim() === "" || !draft.model || draft.model.trim() === "") {
    currentIssues.push({
      rowNumber: draft.rowNumber,
      column: "Fabricante/Modelo",
      severity: "ERROR",
      message: "Fabricante e Modelo são obrigatórios."
    });
  }
  const hasBlockingError = currentIssues.some(i => i.severity === "ERROR");

  if (hasBlockingError) {
    return {
      rowNumber: draft.rowNumber,
      sheetName: draft.sheetName,
      assetType: draft.assetType,
      draft,
      status: "ERROR",
      issues: currentIssues,
      existingAsset,
      isBlocked: true
    };
  }

  // Se não existe na base, é NOVO
  if (!existingAsset) {
    return {
      rowNumber: draft.rowNumber,
      sheetName: draft.sheetName,
      assetType: draft.assetType,
      draft,
      status: "NEW",
      issues,
      isBlocked: false,
      changesDescription: ["Novo equipamento a ser cadastrado na base de dados."]
    };
  }

  // Equipamento existe: verifica se há novo certificado de calibração
  const latestCert = existingCertificates.length > 0 ? existingCertificates[0] : null;
  const draftCert = draft.certificate;

  const changes: string[] = [];
  let isNewCert = false;

  if (draftCert && draftCert.certificateNumber && draftCert.certificateNumber !== "NÃO INFORMADO") {
    if (!latestCert) {
      isNewCert = true;
      changes.push(`Vincular novo certificado de calibração nº ${draftCert.certificateNumber}.`);
    } else {
      const certNumDiff = draftCert.certificateNumber !== latestCert.certificateNumber;
      const certExpDiff = draftCert.expirationDate && draftCert.expirationDate !== String(latestCert.expirationDate || "").slice(0, 10);
      const certIssueDiff = draftCert.issueDate && draftCert.issueDate !== String(latestCert.issueDate || "").slice(0, 10);

      if (certNumDiff || certExpDiff || certIssueDiff) {
        isNewCert = true;
        changes.push(`Adicionar novo certificado de calibração histórico nº ${draftCert.certificateNumber} (Validade: ${draftCert.expirationDate || "Não informada"}).`);
      }
    }
  }

  // Checa alterações nos atributos cadastrais
  let hasDataChanges = false;
  if (draft.manufacturer && draft.manufacturer !== "NÃO INFORMADO" && draft.manufacturer !== existingAsset.manufacturer) {
    hasDataChanges = true;
    changes.push(`Fabricante: "${existingAsset.manufacturer}" → "${draft.manufacturer}"`);
  }
  if (draft.model && draft.model !== "NÃO INFORMADO" && draft.model !== existingAsset.model) {
    hasDataChanges = true;
    changes.push(`Modelo: "${existingAsset.model}" → "${draft.model}"`);
  }
  if (draft.description && draft.description !== existingAsset.description && !draft.description.startsWith(draft.assetType)) {
    hasDataChanges = true;
    changes.push(`Descrição atualizada.`);
  }

  if (draft.technicalData && Object.keys(draft.technicalData).length > 0) {
    const existingTd = (existingAsset.technicalData || {}) as Record<string, any>;
    for (const [k, v] of Object.entries(draft.technicalData)) {
      if (v !== undefined && v !== null && v !== "" && existingTd[k] !== v) {
        hasDataChanges = true;
        const label = k === 'measurementRangeMax' ? 'Faixa Máx (mm)' : (k === 'measurementRangeMin' ? 'Faixa Mín (mm)' : k);
        changes.push(`${label}: "${existingTd[k] ?? '-'}" → "${v}"`);
      }
    }
  }

  if (isNewCert) {
    return {
      rowNumber: draft.rowNumber,
      sheetName: draft.sheetName,
      assetType: draft.assetType,
      draft,
      status: "NEW_CALIBRATION",
      issues,
      existingAsset,
      existingCertificate: latestCert,
      changesDescription: changes,
      isBlocked: false
    };
  }

  if (hasDataChanges) {
    return {
      rowNumber: draft.rowNumber,
      sheetName: draft.sheetName,
      assetType: draft.assetType,
      draft,
      status: "UPDATE",
      issues,
      existingAsset,
      existingCertificate: latestCert,
      changesDescription: changes,
      isBlocked: false
    };
  }

  return {
    rowNumber: draft.rowNumber,
    sheetName: draft.sheetName,
    assetType: draft.assetType,
    draft,
    status: "UNCHANGED",
    issues,
    existingAsset,
    existingCertificate: latestCert,
    changesDescription: ["Nenhuma alteração detectada em relação ao cadastro existente."],
    isBlocked: false
  };
}

// ============================================================================
// GERADOR DE MODELO DE PLANILHA (XLSX E CSV ZERO-DEPENDENCY)
// ============================================================================

export interface TemplateSheetDef {
  readonly sheetName: string;
  readonly headers: readonly string[];
  readonly rows: readonly (readonly string[])[];
}

export const NDT_IMPORT_TEMPLATE_SHEETS: readonly TemplateSheetDef[] = [
  {
    sheetName: "01_ME-US",
    headers: [
      "Código Interno", "Fabricante", "Modelo", "Nº de Série", "Descrição",
      "Faixa Medição Mín (mm)", "Faixa Medição Máx (mm)", "Velocidade Sonora (m/s)", "Resolução (mm)",
      "Patrimônio", "Observações",
      "Nº Certificado Calibração", "Órgão / Laboratório Emissor", "Data da Calibração", "Data de Validade"
    ],
    rows: [
      [
        "ME-01", "Olympus", "38DL Plus", "SN-38DL-8821", "Medidor de Espessura por Ultrassom Digital",
        "0.50", "500.00", "5920", "0.01",
        "PAT-00412", "Equipamento principal de campo",
        "CAL-2025-081", "Lab MetroCalib BR", "15/01/2025", "15/01/2026"
      ],
      [
        "ME-02", "Krautkramer / GE", "DM5E", "SN-DM5E-3310", "Medidor de Espessura Ultrassônico",
        "0.60", "400.00", "5900", "0.01",
        "PAT-00413", "Equipamento reserva",
        "CAL-2024-912", "Lab Brasil Calibrações", "10/06/2024", "10/06/2025"
      ]
    ]
  },
  {
    sheetName: "02_TRANSDUTORES",
    headers: [
      "Código Interno", "Fabricante", "Modelo", "Nº de Série", "Descrição",
      "Tipo Transdutor", "Frequência (MHz)", "Diâmetro (mm)", "Ângulo (º)", "Conector",
      "Patrimônio", "Observações",
      "Nº Certificado Calibração", "Órgão / Laboratório Emissor", "Data da Calibração", "Data de Validade"
    ],
    rows: [
      [
        "TR-01", "Olympus", "D790-SM", "SN-TR-5541", "Transdutor Duplo Cristal para Medição de Espessura",
        "DUAL_ELEMENT", "5.0", "11.0", "0", "Lemo 00",
        "PAT-00501", "Uso geral em tubulações industriais",
        "CERT-TR-881", "Lab MetroCalib BR", "15/01/2025", "15/01/2026"
      ],
      [
        "TR-02", "Olympus", "V260-SM", "SN-TR-9922", "Transdutor Alta Temperatura Linha de Atraso",
        "DELAY_LINE", "7.5", "6.35", "0", "Microdot",
        "PAT-00502", "Operação até 250ºC",
        "CERT-TR-882", "Lab MetroCalib BR", "20/02/2025", "20/02/2026"
      ]
    ]
  },
  {
    sheetName: "03_YOKES",
    headers: [
      "Código Interno", "Fabricante", "Modelo", "Nº de Série", "Descrição",
      "Tipo de Corrente", "Tensão de Alimentação (V)", "Espaçamento Polos Mín (mm)", "Espaçamento Polos Máx (mm)", "Peso (kg)",
      "Patrimônio", "Observações",
      "Nº Certificado Calibração", "Órgão / Laboratório Emissor", "Data da Calibração", "Data de Validade"
    ],
    rows: [
      [
        "YK-01", "Magnaflux", "Y-7", "SN-YK-7714", "Yoke Eletromagnético Articulado AC/DC",
        "AC/DC", "220V", "25", "250", "3.4",
        "PAT-00601", "Ensaio por Partículas Magnéticas",
        "CERT-YK-410", "Lab Partículas Brasil", "05/03/2025", "05/03/2026"
      ]
    ]
  },
  {
    sheetName: "04_B-SCAN",
    headers: [
      "Código Interno", "Fabricante", "Modelo", "Nº de Série", "Descrição",
      "Tecnologia", "Faixa Mín (mm)", "Faixa Máx (mm)", "Resolução Encoder (mm)", "Versão Software",
      "Patrimônio", "Observações",
      "Nº Certificado Calibração", "Órgão / Laboratório Emissor", "Data da Calibração", "Data de Validade"
    ],
    rows: [
      [
        "BSC-01", "Silverwing", "RMS2-600", "SN-BSC-102", "Scanner Automatizado para Mapeamento B-Scan",
        "AUTOMATED_SCANNER", "1.0", "100.0", "0.5", "v4.2.1",
        "PAT-00701", "Mapeamento contínuo de corrosão",
        "CERT-BSC-09", "Silverwing Calibration Lab", "12/01/2025", "12/01/2026"
      ]
    ]
  },
  {
    sheetName: "05_BLOCOS_CALIBRACAO",
    headers: [
      "Código Interno", "Nº de Série / Identificação", "Material", "Espessura Total (mm)", "Espessuras dos Degraus (mm)",
      "Fabricante", "Modelo", "Descrição",
      "Patrimônio", "Observações",
      "Nº Certificado Calibração", "Órgão / Laboratório Emissor", "Data da Calibração", "Data de Validade"
    ],
    rows: [
      [
        "BL-01", "SN-BL-5D-11", "Aço Carbono 1020", "25.0", "2.5, 5.0, 10.0, 15.0, 20.0, 25.0",
        "Metal-Chek", "5 Degraus", "Bloco Escalonado 5 Degraus Aço Carbono",
        "PAT-00801", "Padrão de referência para ME",
        "CERT-BL-552", "Lab Padrões Nacionais", "18/01/2025", "18/01/2026"
      ]
    ]
  },
  {
    sheetName: "06_PIROMETROS",
    headers: [
      "Código Interno", "Fabricante", "Modelo", "Nº de Série", "Descrição",
      "Faixa Temperatura Mínima (°C)", "Faixa Temperatura Máxima (°C)",
      "Emissividade", "Resolução (°C)", "Exatidão", "Distância Ponto",
      "Patrimônio", "Observações", "Nº Certificado Calibração",
      "Órgão / Laboratório Emissor", "Data da Calibração", "Data de Validade"
    ],
    rows: [[
      "PI-01", "Fabricante exemplo", "Modelo exemplo", "SN-PI-001", "Pirômetro infravermelho",
      "-20", "550", "0.95", "0.1", "±2 °C", "12:1", "PAT-001", "Modelo para importação",
      "CERT-PI-001", "Laboratório exemplo", "01/01/2026", "01/01/2027"
    ]]
  }
];

/**
 * Constrói um buffer ZIP .xlsx completo com as 6 abas padronizadas
 * de equipamentos de END sem nenhuma biblioteca externa.
 */
export function buildNdtEquipmentXlsxTemplate(): Uint8Array {
  const strings: string[] = [];
  const stringIndex = new Map<string, number>();

  const si = (value: string): number => {
    const existing = stringIndex.get(value);
    if (existing !== undefined) return existing;
    const index = strings.length;
    strings.push(value);
    stringIndex.set(value, index);
    return index;
  };

  const colLetter = (index: number): string => {
    let n = index + 1, out = "";
    while (n > 0) {
      const rem = (n - 1) % 26;
      out = String.fromCharCode(65 + rem) + out;
      n = Math.floor((n - 1) / 26);
    }
    return out;
  };

  const escapeXml = (value: string) => String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  const sheetXmls = NDT_IMPORT_TEMPLATE_SHEETS.map(spec => {
    const all = [spec.headers, ...spec.rows];
    const rowsXml = all.map((cells, rowIndex) => {
      const cellXml = cells.map((val, colIndex) => {
        if (!val) return "";
        return `<x:c r="${colLetter(colIndex)}${rowIndex + 1}" t="s"><x:v>${si(val)}</x:v></x:c>`;
      }).join("");
      return `<x:row r="${rowIndex + 1}">${cellXml}</x:row>`;
    }).join("");
    return `<?xml version="1.0" encoding="utf-8"?><x:worksheet xmlns:x="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><x:sheetData>${rowsXml}</x:sheetData></x:worksheet>`;
  });

  const files: Array<{ name: string; content: string }> = [];
  files.push({
    name: "[Content_Types].xml",
    content: `<?xml version="1.0"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="xml" ContentType="application/xml"/><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/sharedStrings.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sharedStrings+xml"/>${NDT_IMPORT_TEMPLATE_SHEETS.map((_, i) => `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`).join("")}</Types>`
  });

  files.push({
    name: "xl/workbook.xml",
    content: `<?xml version="1.0" encoding="utf-8"?><x:workbook xmlns:x="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><x:sheets>${NDT_IMPORT_TEMPLATE_SHEETS.map((s, i) => `<x:sheet name="${escapeXml(s.sheetName)}" sheetId="${i + 1}" r:id="R${i}" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"/>`).join("")}</x:sheets></x:workbook>`
  });

  files.push({
    name: "xl/_rels/workbook.xml.rels",
    content: `<?xml version="1.0" encoding="utf-8"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">${NDT_IMPORT_TEMPLATE_SHEETS.map((_, i) => `<Relationship Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="/xl/worksheets/sheet${i + 1}.xml" Id="R${i}"/>`).join("")}<Relationship Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/sharedStrings" Target="/xl/sharedStrings.xml" Id="RS"/></Relationships>`
  });

  files.push({
    name: "_rels/.rels",
    content: `<?xml version="1.0" encoding="utf-8"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml" Id="rId1"/></Relationships>`
  });

  NDT_IMPORT_TEMPLATE_SHEETS.forEach((_, i) => {
    const xml = sheetXmls[i];
    if (xml) {
      files.push({ name: `xl/worksheets/sheet${i + 1}.xml`, content: xml });
    }
  });

  files.push({
    name: "xl/sharedStrings.xml",
    content: `<?xml version="1.0" encoding="utf-8"?><x:sst xmlns:x="http://schemas.openxmlformats.org/spreadsheetml/2006/main" count="${strings.length}" uniqueCount="${strings.length}">${strings.map(s => `<x:si><x:t>${escapeXml(s)}</x:t></x:si>`).join("")}</x:sst>`
  });

  return zipStore(files);
}

/**
 * M21 F — Exporta os equipamentos efetivamente cadastrados em XLSX (OpenXML).
 * Diferentemente do modelo de importação, não inclui linhas demonstrativas.
 * Textos são armazenados como SharedStrings, nunca como fórmulas.
 */
export function buildNdtAssetsXlsxExport(assets: readonly import("./ndt-assets.js").NdtAsset[]): Uint8Array {
  if (assets.length > 10000) throw new RangeError("Exportação limitada a 10.000 equipamentos por arquivo.");
  const columns = ["Código interno", "Tipo", "Fabricante", "Modelo", "Número de série", "Situação", "Descrição"];
  const rows: string[][] = [columns, ...assets.map(a => [
    a.internalCode ?? "", a.assetType ?? "", a.manufacturer ?? "", a.model ?? "",
    a.serialNumber ?? "", a.status ?? "", a.description ?? ""
  ])];
  const shared: string[] = [];
  const positions = new Map<string, number>();
  const idx = (value: string) => {
    let found = positions.get(value);
    if (found === undefined) {
      found = shared.length;
      shared.push(value);
      positions.set(value, found);
    }
    return found;
  };
  const xmlEsc = (value: string) => value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const sheetData = rows.map((row, n) => `<x:row r="${n + 1}">${row.map((value, j) => `<x:c r="${String.fromCharCode(65 + j)}${n + 1}" t="s"><x:v>${idx(String(value))}</x:v></x:c>`).join("")}</x:row>`).join("");
  const files = [
    {name:"[Content_Types].xml",content:'<?xml version="1.0"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="xml" ContentType="application/xml"/><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/sharedStrings.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sharedStrings+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/></Types>'},
    {name:"xl/workbook.xml",content:'<?xml version="1.0"?><x:workbook xmlns:x="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><x:sheets><x:sheet name="Equipamentos END" sheetId="1" r:id="R1" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"/></x:sheets></x:workbook>'},
    {name:"xl/_rels/workbook.xml.rels",content:'<?xml version="1.0"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="/xl/worksheets/sheet1.xml" Id="R1"/><Relationship Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/sharedStrings" Target="/xl/sharedStrings.xml" Id="RS"/></Relationships>'},
    {name:"_rels/.rels",content:'<?xml version="1.0"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml" Id="rId1"/></Relationships>'},
    {name:"xl/worksheets/sheet1.xml",content:`<?xml version="1.0" encoding="utf-8"?><x:worksheet xmlns:x="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><x:sheetData>${sheetData}</x:sheetData></x:worksheet>`},
    {name:"xl/sharedStrings.xml",content:`<?xml version="1.0" encoding="utf-8"?><x:sst xmlns:x="http://schemas.openxmlformats.org/spreadsheetml/2006/main" count="${shared.length}" uniqueCount="${shared.length}">${shared.map(s => `<x:si><x:t>${xmlEsc(s)}</x:t></x:si>`).join("")}</x:sst>`}
  ];
  return zipStore(files);
}

/**
 * Gera conteúdo CSV para uma aba específica de equipamento de END.
 */
export function buildNdtEquipmentCsvTemplate(assetType: NdtAssetType): string {
  const spec = NDT_IMPORT_TEMPLATE_SHEETS.find(s => {
    const mapped = resolveSheetAssetType(s.sheetName);
    return mapped === assetType;
  }) || NDT_IMPORT_TEMPLATE_SHEETS[0]!;

  const escapeCsv = (val: string) => {
    if (val.includes(";") || val.includes('"') || val.includes("\n")) {
      return `"${val.replace(/"/g, '""')}"`;
    }
    return val;
  };

  const headerLine = (spec.headers as readonly string[]).map(escapeCsv).join(";");
  const dataLines = (spec.rows as ReadonlyArray<readonly string[]>).map(row => row.map(escapeCsv).join(";"));
  return "\uFEFF" + [headerLine, ...dataLines].join("\r\n");
}

function zipStore(files: ReadonlyArray<{ name: string; content: string }>): Uint8Array {
  const encoder = new TextEncoder();
  const chunks: number[] = [];
  const central: number[] = [];
  const push = (target: number[], ...bytes: number[]) => target.push(...bytes);
  const u16 = (target: number[], value: number) => push(target, value & 0xff, (value >> 8) & 0xff);
  const u32 = (target: number[], value: number) => push(target, value & 0xff, (value >> 8) & 0xff, (value >> 16) & 0xff, (value >>> 24) & 0xff);

  const crcTable: number[] = [];
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    crcTable[i] = c >>> 0;
  }
  const crc32 = (data: Uint8Array): number => {
    let c = 0xffffffff;
    for (const byte of data) c = crcTable[(c ^ byte) & 0xff]! ^ (c >>> 8);
    return (c ^ 0xffffffff) >>> 0;
  };

  for (const file of files) {
    const nameBytes = encoder.encode(file.name);
    const data = encoder.encode(file.content);
    const localOffset = chunks.length;
    const crc = crc32(data);

    u32(chunks, 0x04034b50); u16(chunks, 20); u16(chunks, 0); u16(chunks, 0); u16(chunks, 0); u16(chunks, 0);
    u32(chunks, crc); u32(chunks, data.length); u32(chunks, data.length);
    u16(chunks, nameBytes.length); u16(chunks, 0);
    for (const byte of nameBytes) chunks.push(byte);
    for (const byte of data) chunks.push(byte);

    u32(central, 0x02014b50); u16(central, 20); u16(central, 20); u16(central, 0); u16(central, 0); u16(central, 0); u16(central, 0);
    u32(central, crc); u32(central, data.length); u32(central, data.length);
    u16(central, nameBytes.length); u16(central, 0); u16(central, 0); u16(central, 0); u16(central, 0);
    u32(central, 0);
    u32(central, localOffset);
    push(central, ...nameBytes);
  }

  const centralOffset = chunks.length;
  const out = [...chunks, ...central];
  u32(out, 0x06054b50); u16(out, 0); u16(out, 0); u16(out, files.length); u16(out, files.length);
  u32(out, central.length); u32(out, centralOffset); u16(out, 0);
  return new Uint8Array(out);
}
